(() => {
  class MultimediaLicenseCentre {
    constructor(root) {
      this.root = root;
      this.token = root.dataset.storefrontToken;
      this.apiVersion = root.dataset.apiVersion || '2026-07';
      this.metaobjectType = root.dataset.metaobjectType || 'license';
      this.cacheMinutes = Number(root.dataset.cacheMinutes || 30);
      this.directory = root.querySelector('[data-mlc-directory]');
      this.loadingPage = root.querySelector('[data-mlc-loading-page]');
      this.loadingStatus = root.querySelector('[data-mlc-loading-status]');
      this.loadingProgress = root.querySelector('[data-mlc-loading-progress]');
      this.loadingProgressBar = root.querySelector('[data-mlc-loading-progress-bar]');
      this.loadingPercent = root.querySelector('[data-mlc-loading-percent]');
      this.loadingEta = root.querySelector('[data-mlc-loading-eta]');
      this.loadingStartedAt = performance.now();
      this.loadingLastProgress = 0;
      this.loadingLastProgressAt = this.loadingStartedAt;
      this.loadingRates = [];
      this.detail = root.querySelector('[data-mlc-detail]');
      this.grid = root.querySelector('[data-mlc-grid]');
      this.search = root.querySelector('[data-mlc-search]');
      this.count = root.querySelector('[data-mlc-count]');
      this.status = root.querySelector('[data-mlc-status]');
      this.empty = root.querySelector('[data-mlc-empty]');
      this.setup = root.querySelector('[data-mlc-setup]');
      this.productRoot = root.querySelector('[data-mmc]');
      this.licenses = [];
      this.licenseByHandle = new Map();
      this.productIndex = null;
      this.productIndexPromise = null;
      this.productLoadGeneration = 0;
      this.currentLicense = null;
      this.currentFranchise = null;
      this.currentView = 'directory';
      this.packFiles = [];
      this.activePackFileIndex = -1;
      this.selectedPackFiles = new Set();

      if (!this.directory || !this.detail) return;
      this.bind();
      this.start();
    }

    bind() {
      this.root.addEventListener('click', (event) => {
        const franchiseCard = event.target.closest('[data-mlc-franchise-card]');
        if (franchiseCard) {
          this.openFranchise(franchiseCard.dataset.licenseHandle, franchiseCard.dataset.handle, true);
          return;
        }

        const card = event.target.closest('[data-mlc-card]');
        if (card) {
          this.openLicense(card.dataset.handle, true);
          return;
        }

        if (event.target.closest('[data-mlc-back]')) {
          if (this.currentView === 'franchise' && this.currentLicense) this.openLicense(this.currentLicense.handle, true);
          else this.showDirectory(true);
          return;
        }

        const logoButton = event.target.closest('[data-mlc-download-logo]');
        if (logoButton) this.downloadLogo(logoButton);

        const packButton = event.target.closest('[data-mlc-pack-download]');
        if (packButton) this.downloadPackFile(packButton);

        const packOpen = event.target.closest('[data-mlc-pack-open]');
        if (packOpen) this.openPackModal(packOpen.dataset.fileId);

        if (event.target.closest('[data-mlc-pack-previous]')) {
          this.movePackModal(-1);
          return;
        }

        if (event.target.closest('[data-mlc-pack-next]')) {
          this.movePackModal(1);
          return;
        }

        if (event.target.closest('[data-mlc-pack-modal-close]')) this.closePackModal();

        const downloadAllPack = event.target.closest('[data-mlc-pack-download-all]');
        if (downloadAllPack) this.openPackDownloadConfirmation(this.packFiles, downloadAllPack);

        const downloadSelectedPack = event.target.closest('[data-mlc-pack-download-selected]');
        if (downloadSelectedPack) {
          const files = this.packFiles.filter((file) => this.selectedPackFiles.has(file.id));
          this.openPackDownloadConfirmation(files, downloadSelectedPack);
        }

        if (event.target.closest('[data-mlc-pack-clear]')) this.clearPackSelection();

        if (event.target.closest('[data-mlc-pack-confirm-cancel]')) {
          this.closePackDownloadConfirmation();
          return;
        }

        if (event.target.closest('[data-mlc-pack-confirm-continue]')) {
          const pending = this.pendingPackDownload;
          this.closePackDownloadConfirmation();
          if (pending) this.downloadPackZip(pending.files, pending.button);
        }
      });

      this.root.addEventListener('change', (event) => {
        const checkbox = event.target.closest('[data-mlc-pack-select]');
        if (!checkbox) return;
        if (checkbox.checked) this.selectedPackFiles.add(checkbox.dataset.fileId);
        else this.selectedPackFiles.delete(checkbox.dataset.fileId);
        this.updatePackSelectionControls();
      });

      const packModal = this.root.querySelector('[data-mlc-pack-modal]');
      packModal?.addEventListener('click', (event) => {
        if (event.target === event.currentTarget) this.closePackModal();
      });
      packModal?.addEventListener('close', () => document.body.classList.remove('mmc-modal-open'));

      this.root.addEventListener('keydown', (event) => {
        if (!packModal?.open) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.movePackModal(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.movePackModal(1);
        }
      });

      const packConfirm = this.root.querySelector('[data-mlc-pack-confirm]');
      packConfirm?.addEventListener('click', (event) => {
        if (event.target === event.currentTarget) this.closePackDownloadConfirmation();
      });
      packConfirm?.addEventListener('close', () => {
        this.pendingPackDownload = null;
      });

      this.search?.addEventListener('input', () => this.filterDirectory());

      window.addEventListener('popstate', () => {
        const params = new URL(window.location.href).searchParams;
        const handle = this.getLicenseHandle(params);
        const franchise = params.get('franchise');
        if (handle && franchise) this.openFranchise(handle, franchise, false);
        else if (handle) this.openLicense(handle, false);
        else this.showDirectory(false);
      });
    }

    async start() {
      if (!this.token) {
        this.setup.hidden = false;
        this.count.textContent = 'License directory unavailable';
        this.loadingPage.hidden = true;
        this.directory.hidden = false;
        return;
      }

      try {
        this.updateLoadingProgress(5, 'Checking your license library…');
        this.licenses = await this.loadAllLicenses();
        this.updateLoadingProgress(84, `Organising ${this.licenses.length} licenses…`);
        this.licenses.sort((a, b) => a.title.localeCompare(b.title));
        this.licenseByHandle = new Map(this.licenses.map((license) => [license.handle, license]));
        this.renderDirectory();
        this.updateLoadingProgress(88, 'Preparing the product library…');
        await this.loadProductIndex(this.loadingStatus);
        this.updateLoadingProgress(96, 'Finishing your Multimedia License Centre…', 1);

        const params = new URL(window.location.href).searchParams;
        const requestedHandle = this.getLicenseHandle(params);
        const requestedFranchise = params.get('franchise');
        if (requestedHandle && requestedFranchise) await this.openFranchise(requestedHandle, requestedFranchise, false);
        else if (requestedHandle) await this.openLicense(requestedHandle, false);
        else this.directory.hidden = false;
        this.updateLoadingProgress(100, 'Your Multimedia License Centre is ready.', 0);
        this.loadingPage.hidden = true;
      } catch (error) {
        console.error('[Multimedia License Centre]', error);
        this.status.textContent = error.message;
        this.count.textContent = 'Unable to load licenses';
        this.loadingPage.hidden = true;
        this.directory.hidden = false;
      }
    }

    updateLoadingProgress(value, message, etaSeconds = null) {
      const rounded = Math.max(0, Math.min(100, Math.round(value)));
      const now = performance.now();
      const elapsedSeconds = Math.max(0, (now - this.loadingLastProgressAt) / 1000);
      const progressIncrease = value - this.loadingLastProgress;

      if (
        !Number.isFinite(etaSeconds)
        && progressIncrease > 0
        && progressIncrease <= 15
        && elapsedSeconds >= 0.25
        && value < 96
      ) {
        const progressPerSecond = progressIncrease / elapsedSeconds;
        if (Number.isFinite(progressPerSecond) && progressPerSecond > 0) {
          this.loadingRates.push(progressPerSecond);
          if (this.loadingRates.length > 5) this.loadingRates.shift();
          const sortedRates = [...this.loadingRates].sort((a, b) => a - b);
          const middle = Math.floor(sortedRates.length / 2);
          const typicalRate = sortedRates.length % 2
            ? sortedRates[middle]
            : (sortedRates[middle - 1] + sortedRates[middle]) / 2;
          etaSeconds = Math.max(1, (96 - value) / typicalRate);
        }
      }

      this.loadingLastProgress = value;
      this.loadingLastProgressAt = now;
      if (this.loadingStatus && message) this.loadingStatus.textContent = message;
      if (this.loadingProgress) this.loadingProgress.setAttribute('aria-valuenow', rounded);
      if (this.loadingProgressBar) this.loadingProgressBar.style.width = `${rounded}%`;
      if (this.loadingPercent) this.loadingPercent.textContent = `${rounded}%`;
      if (this.loadingEta) {
        if (rounded >= 100) this.loadingEta.textContent = 'Ready';
        else if (Number.isFinite(etaSeconds) && etaSeconds > 0) {
          const seconds = Math.max(1, Math.round(etaSeconds));
          this.loadingEta.textContent = seconds < 60 ? `About ${seconds} seconds remaining` : `About ${Math.ceil(seconds / 60)} minutes remaining`;
        } else this.loadingEta.textContent = 'Estimating time remaining…';
      }
    }

    getLicenseHandle(params) {
      return params.get('license') || params.get(['lic', 'ence'].join(''));
    }

    openCacheDatabase() {
      if (!('indexedDB' in window)) return Promise.resolve(null);
      if (this.cacheDatabasePromise) return this.cacheDatabasePromise;
      this.cacheDatabasePromise = new Promise((resolve) => {
        const request = indexedDB.open('rubies-mmc-cache', 1);
        request.onupgradeneeded = () => {
          if (!request.result.objectStoreNames.contains('catalogues')) request.result.createObjectStore('catalogues');
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
        request.onblocked = () => resolve(null);
      });
      return this.cacheDatabasePromise;
    }

    async readCache(key) {
      const database = await this.openCacheDatabase();
      if (!database) return null;
      return new Promise((resolve) => {
        const transaction = database.transaction('catalogues', 'readonly');
        const request = transaction.objectStore('catalogues').get(key);
        request.onsuccess = () => {
          const cached = request.result;
          resolve(cached?.savedAt && Date.now() - cached.savedAt <= this.cacheMinutes * 60000 ? cached.value : null);
        };
        request.onerror = () => resolve(null);
        transaction.onabort = () => resolve(null);
      });
    }

    async writeCache(key, value) {
      const database = await this.openCacheDatabase();
      if (!database) return;
      await new Promise((resolve) => {
        const transaction = database.transaction('catalogues', 'readwrite');
        transaction.objectStore('catalogues').put({ savedAt: Date.now(), value }, key);
        transaction.oncomplete = resolve;
        transaction.onerror = resolve;
        transaction.onabort = resolve;
      });
    }

    async loadAllLicenses() {
      const cached = await this.readCache('rubies-mlc-directory-v1');
      if (Array.isArray(cached)) {
        this.status.textContent = 'Loading saved license library…';
        this.updateLoadingProgress(85, `Loading ${cached.length} saved licenses…`, 1);
        return cached;
      }
      const licenses = [];
      let after = null;
      let hasNextPage = true;
      let page = 1;

      while (hasNextPage) {
        this.status.textContent = `Loading license batch ${page}…`;
        this.updateLoadingProgress(Math.min(45, 8 + (page - 1) * 12), 'Preparing licenses…');
        const data = await this.storefrontQuery(this.licenseQuery(), { after, type: this.metaobjectType });
        const connection = data.metaobjects;

        connection.nodes.forEach((node) => {
          const title = node.title?.value?.trim();
          if (!title) return;
          licenses.push({
            id: node.id,
            handle: node.handle,
            title,
            description: node.description?.value || '',
            logoUrl: node.logo?.reference?.image?.url || '',
            logoAlt: node.logo?.reference?.image?.altText || title,
            licensePack: this.mediaFilesFromField(node.licensePack),
            franchiseIds: this.referenceIds(node.franchises?.value),
            franchises: []
          });
        });

        hasNextPage = connection.pageInfo.hasNextPage;
        after = connection.pageInfo.endCursor;
        page += 1;
      }

      const franchiseById = await this.loadAllFranchises();
      this.updateLoadingProgress(82, 'Linking licenses and franchises…', 1);
      licenses.forEach((license) => {
        license.franchises = license.franchiseIds
          .map((id) => franchiseById.get(id))
          .filter(Boolean)
          .sort((a, b) => a.title.localeCompare(b.title));
      });
      this.status.textContent = '';
      await this.writeCache('rubies-mlc-directory-v1', licenses);
      return licenses;
    }

    licenseQuery() {
      return `
        query MultimediaLicenses($type: String!, $after: String) {
          metaobjects(type: $type, first: 100, after: $after) {
            nodes {
              id
              handle
              title: field(key: "title") { value }
              description: field(key: "description") { value }
              logo: field(key: "logo") {
                reference {
                  ... on MediaImage {
                    image { url altText width height }
                  }
                }
              }
              franchises: field(key: "franchise") { value }
              licensePack: field(key: "license_pack") {
                reference { ...MlcDownloadFile }
                references(first: 50) { nodes { ...MlcDownloadFile } }
              }
            }
            pageInfo { hasNextPage endCursor }
          }
        }

        fragment MlcDownloadFile on MetafieldReference {
          ... on MediaImage {
            id
            image { url altText width height }
          }
          ... on Video {
            id
            alt
            previewImage { url altText width height }
            sources { url mimeType format width height }
          }
          ... on GenericFile {
            id
            alt
            url
            mimeType
            originalFileSize
            previewImage { url altText width height }
          }
        }
      `;
    }

    mediaFilesFromField(field) {
      const references = [field?.reference, ...(field?.references?.nodes || [])].filter(Boolean);
      const unique = new Map();

      references.forEach((file, index) => {
        if (unique.has(file.id)) return;
        if (file.image?.url) {
          unique.set(file.id, {
            id: file.id,
            type: 'image',
            url: file.image.url,
            previewUrl: file.image.url,
            title: file.image.altText || this.filenameFromUrl(file.image.url, `Image ${index + 1}`),
            mimeType: '',
            width: file.image.width || null,
            height: file.image.height || null,
            fileSize: null
          });
          return;
        }

        if (file.sources?.length) {
          const sources = [...file.sources].sort((a, b) => (b.width || 0) - (a.width || 0));
          const source = sources.find((item) => item.mimeType === 'video/mp4') || sources[0];
          if (!source?.url) return;
          unique.set(file.id, {
            id: file.id,
            type: 'video',
            url: source.url,
            previewUrl: file.previewImage?.url || '',
            title: file.alt || this.filenameFromUrl(source.url, `Video ${index + 1}`),
            mimeType: source.mimeType || 'video/mp4',
            width: source.width || null,
            height: source.height || null,
            fileSize: null
          });
          return;
        }

        if (file.url) {
          unique.set(file.id, {
            id: file.id,
            type: file.mimeType?.startsWith('video/') ? 'video' : 'file',
            url: file.url,
            previewUrl: file.previewImage?.url || '',
            title: file.alt || this.filenameFromUrl(file.url, `File ${index + 1}`),
            mimeType: file.mimeType || '',
            width: file.previewImage?.width || null,
            height: file.previewImage?.height || null,
            fileSize: Number(file.originalFileSize) || null
          });
        }
      });

      return [...unique.values()];
    }

    filenameFromUrl(url, fallback) {
      try {
        return decodeURIComponent(new URL(url, window.location.origin).pathname.split('/').pop()) || fallback;
      } catch (error) {
        return fallback;
      }
    }

    referenceIds(value) {
      if (!value) return [];
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch (error) {
        return [value];
      }
    }

    async loadAllFranchises() {
      const franchises = new Map();
      let after = null;
      let hasNextPage = true;
      let page = 1;

      while (hasNextPage) {
        this.status.textContent = `Loading franchise batch ${page}…`;
        this.updateLoadingProgress(Math.min(78, 52 + (page - 1) * 10), 'Preparing franchises…');
        const data = await this.storefrontQuery(this.franchiseQuery(), { after, type: 'franchise' });
        data.metaobjects.nodes.forEach((node) => {
          const title = node.title?.value?.trim();
          if (!title) return;
          franchises.set(node.id, {
            id: node.id,
            handle: node.handle,
            title,
            textOverride: node.textOverride?.value || '',
            description: '',
            logoUrl: node.logo?.reference?.image?.url || '',
            logoAlt: node.logo?.reference?.image?.altText || title,
            licensePack: this.mediaFilesFromField(node.licensePack)
          });
        });
        hasNextPage = data.metaobjects.pageInfo.hasNextPage;
        after = data.metaobjects.pageInfo.endCursor;
        page += 1;
      }
      return franchises;
    }

    franchiseQuery() {
      return `
        query MultimediaFranchises($type: String!, $after: String) {
          metaobjects(type: $type, first: 100, after: $after) {
            nodes {
              id
              handle
              title: field(key: "title") { value }
              textOverride: field(key: "text_override") { value }
              logo: field(key: "logo") {
                reference {
                  ... on MediaImage {
                    image { url altText width height }
                  }
                }
              }
              licensePack: field(key: "license_pack") {
                reference { ...MlcFranchiseDownloadFile }
                references(first: 50) { nodes { ...MlcFranchiseDownloadFile } }
              }
            }
            pageInfo { hasNextPage endCursor }
          }
        }

        fragment MlcFranchiseDownloadFile on MetafieldReference {
          ... on MediaImage {
            id
            image { url altText width height }
          }
          ... on Video {
            id
            alt
            previewImage { url altText width height }
            sources { url mimeType format width height }
          }
          ... on GenericFile {
            id
            alt
            url
            mimeType
            originalFileSize
            previewImage { url altText width height }
          }
        }
      `;
    }

    async storefrontQuery(query, variables) {
      const response = await fetch(`/api/${this.apiVersion}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.token
        },
        body: JSON.stringify({ query, variables })
      });

      if (!response.ok) throw new Error(`Storefront API request failed (${response.status}).`);
      const payload = await response.json();
      if (payload.errors?.length) throw new Error(payload.errors.map((error) => error.message).join(' '));
      return payload.data;
    }

    renderDirectory() {
      this.grid.replaceChildren();

      this.licenses.forEach((license) => {
        const article = document.createElement('article');
        article.className = 'mlc-card';
        article.dataset.mlcCard = '';
        article.dataset.handle = license.handle;
        article.dataset.searchTerms = this.normaliseSearch([
          license.title,
          ...license.franchises.map((franchise) => franchise.title)
        ].join(' '));

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mlc-card__button';
        button.setAttribute('aria-label', `View ${license.title} assets`);

        const logoWrap = document.createElement('span');
        logoWrap.className = 'mlc-card__logo-wrap';
        const badge = document.createElement('span');
        badge.className = 'mlc-card__asset-badge';
        badge.textContent = 'Media assets';
        logoWrap.append(badge);
        if (license.logoUrl) {
          const image = document.createElement('img');
          image.className = 'mlc-card__logo';
          image.src = license.logoUrl;
          image.alt = license.logoAlt;
          image.loading = 'lazy';
          logoWrap.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'mlc-card__placeholder';
          placeholder.textContent = 'Logo unavailable';
          logoWrap.append(placeholder);
        }

        const title = document.createElement('span');
        title.className = 'mlc-card__title';
        title.textContent = license.title;
        const action = document.createElement('span');
        action.className = 'mlc-card__asset-action';
        action.innerHTML = '<span class="mlc-card__asset-action-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6.5h6l2 2h10v10.5H3z"></path><path d="M12 11v5m0 0-2-2m2 2 2-2"></path></svg></span><span>View downloadable assets</span>';
        button.append(logoWrap, title, action);
        article.append(button);
        this.grid.append(article);
      });

      this.filterDirectory();
    }

    filterDirectory() {
      const query = this.normaliseSearch(this.search?.value || '');
      const terms = query.split(/\s+/).filter(Boolean);
      let visible = 0;
      this.grid.querySelectorAll('[data-mlc-card]').forEach((card) => {
        const haystack = card.dataset.searchTerms || '';
        const show = terms.length === 0 || terms.every((term) => haystack.includes(term));
        card.hidden = !show;
        if (show) visible += 1;
      });
      this.count.textContent = `Showing ${visible} of ${this.licenses.length} licenses`;
      this.empty.hidden = visible !== 0;
    }

    normaliseSearch(value) {
      return value
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[’'`]/g, '')
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/gi, ' ')
        .trim()
        .toLowerCase();
    }

    async openLicense(handle, updateHistory) {
      const license = this.licenseByHandle.get(handle);
      if (!license) return;

      if (updateHistory) {
        const url = new URL(window.location.href);
        url.searchParams.set('license', handle);
        url.searchParams.delete(['lic', 'ence'].join(''));
        url.searchParams.delete('franchise');
        history.pushState({ license: handle }, '', url);
      }

      this.currentLicense = license;
      this.currentFranchise = null;
      this.currentView = 'license';
      this.directory.hidden = true;
      this.detail.hidden = false;
      this.populateDetailHeader(license, 'license');
      this.renderFranchises(license);
      window.scrollTo({ top: this.root.offsetTop, behavior: 'smooth' });
      await this.loadEntityProducts(license, 'license', 'license');
    }

    async openFranchise(licenseHandle, franchiseHandle, updateHistory) {
      const license = this.licenseByHandle.get(licenseHandle);
      const franchise = license?.franchises.find((item) => item.handle === franchiseHandle);
      if (!license || !franchise) return;

      if (updateHistory) {
        const url = new URL(window.location.href);
        url.searchParams.set('license', licenseHandle);
        url.searchParams.delete(['lic', 'ence'].join(''));
        url.searchParams.set('franchise', franchiseHandle);
        history.pushState({ license: licenseHandle, franchise: franchiseHandle }, '', url);
      }

      this.currentLicense = license;
      this.currentFranchise = franchise;
      this.currentView = 'franchise';
      this.directory.hidden = true;
      this.detail.hidden = false;
      this.populateDetailHeader(franchise, 'franchise');
      this.root.querySelector('[data-mlc-franchises]').hidden = true;
      window.scrollTo({ top: this.root.offsetTop, behavior: 'smooth' });
      await this.loadEntityProducts(franchise, 'franchise', 'franchise');
    }

    showDirectory(updateHistory) {
      if (updateHistory) {
        const url = new URL(window.location.href);
        url.searchParams.delete('license');
        url.searchParams.delete(['lic', 'ence'].join(''));
        url.searchParams.delete('franchise');
        history.pushState({}, '', url);
      }
      this.currentLicense = null;
      this.currentFranchise = null;
      this.currentView = 'directory';
      this.productLoadGeneration += 1;
      this.detail.hidden = true;
      this.directory.hidden = false;
      window.scrollTo({ top: this.root.offsetTop, behavior: 'smooth' });
    }

    populateDetailHeader(entity, entityType) {
      const title = this.root.querySelector('[data-mlc-detail-title]');
      const description = this.root.querySelector('[data-mlc-detail-description]');
      const logo = this.root.querySelector('[data-mlc-detail-logo]');
      const logoButton = this.root.querySelector('[data-mlc-download-logo]');
      const eyebrow = this.root.querySelector('[data-mlc-detail-eyebrow]');
      const back = this.root.querySelector('[data-mlc-back]');
      title.textContent = entity.title;
      description.textContent = entity.description || '';
      description.hidden = !entity.description;
      eyebrow.textContent = `${entityType === 'franchise' ? 'Franchise' : 'License'} resources`;
      back.textContent = entityType === 'franchise' ? `← Back to ${this.currentLicense.title}` : '← Back to all licenses';
      logoButton.textContent = `Download ${entityType} logo`;

      if (entity.logoUrl) {
        logo.src = entity.logoUrl;
        logo.alt = entity.logoAlt;
        logo.hidden = false;
        logoButton.hidden = false;
        logoButton.dataset.url = entity.logoUrl;
        logoButton.dataset.filename = `${this.safeFilename(entity.title)}-logo`;
      } else {
        logo.removeAttribute('src');
        logo.hidden = true;
        logoButton.hidden = true;
      }

      this.renderLicensePack(entity, entityType);
    }

    renderLicensePack(entity, entityType) {
      const section = this.root.querySelector('[data-mlc-license-pack]');
      const heading = this.root.querySelector('[data-mlc-license-pack-heading]');
      const grid = this.root.querySelector('[data-mlc-license-pack-grid]');
      const files = entity.licensePack || [];
      this.packFiles = files.map((file, index) => ({
        ...file,
        filename: this.filenameFromUrl(file.url, `${this.safeFilename(entity.title)}-asset-${index + 1}`)
      }));
      this.selectedPackFiles.clear();
      grid.replaceChildren();
      section.hidden = this.packFiles.length === 0;
      if (!this.packFiles.length) return;

      heading.textContent = `${entityType === 'franchise' ? 'Franchise' : 'License'} pack`;
      this.packFiles.forEach((file, index) => {
        const card = document.createElement('article');
        card.className = 'mlc-pack__item mmc-card';
        card.dataset.fileId = file.id;

        const select = document.createElement('label');
        select.className = 'mmc-card__select mlc-pack__select';
        select.title = `Select ${file.title}`;
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.dataset.mlcPackSelect = '';
        checkbox.dataset.fileId = file.id;
        checkbox.setAttribute('aria-label', `Select ${file.title} for download`);
        const selectBox = document.createElement('span');
        selectBox.className = 'mmc-card__select-box';
        selectBox.setAttribute('aria-hidden', 'true');
        selectBox.innerHTML = '<svg viewBox="0 0 16 16" width="12" height="12"><path d="m3 8 3 3 7-7"></path></svg>';
        select.append(checkbox, selectBox);

        const open = document.createElement('button');
        open.type = 'button';
        open.className = 'mlc-pack__open';
        open.dataset.mlcPackOpen = '';
        open.dataset.fileId = file.id;
        open.setAttribute('aria-label', `View ${file.title}`);
        const preview = document.createElement('span');
        preview.className = 'mlc-pack__preview';

        if (file.previewUrl) {
          const image = document.createElement('img');
          image.src = this.shopifyImageUrl(file.previewUrl, 600);
          image.alt = file.title;
          image.loading = 'lazy';
          preview.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'mlc-pack__file-icon';
          placeholder.textContent = 'FILE';
          preview.append(placeholder);
        }

        if (file.type === 'video') {
          const badge = document.createElement('span');
          badge.className = 'mlc-pack__video-badge';
          badge.textContent = '▶';
          preview.append(badge);
        }

        const content = document.createElement('span');
        content.className = 'mlc-pack__content';
        const title = document.createElement('span');
        title.className = 'mlc-pack__title';
        title.textContent = file.title || `Pack asset ${index + 1}`;
        const action = document.createElement('span');
        action.className = 'mlc-pack__action';
        action.textContent = 'View and download';
        content.append(title, action);
        open.append(preview, content);
        card.append(select, open);
        grid.append(card);
      });
      this.updatePackSelectionControls();
    }

    updatePackSelectionControls() {
      const count = this.root.querySelector('[data-mlc-pack-selection-count]');
      const clear = this.root.querySelector('[data-mlc-pack-clear]');
      const download = this.root.querySelector('[data-mlc-pack-download-selected]');
      const selected = this.selectedPackFiles.size;
      count.textContent = `${selected} selected`;
      clear.disabled = selected === 0;
      download.disabled = selected === 0;
    }

    clearPackSelection() {
      this.selectedPackFiles.clear();
      this.root.querySelectorAll('[data-mlc-pack-select]').forEach((checkbox) => {
        checkbox.checked = false;
      });
      this.updatePackSelectionControls();
    }

    openPackDownloadConfirmation(files, button) {
      if (!files.length || button.disabled) return;
      const dialog = this.root.querySelector('[data-mlc-pack-confirm]');
      const summary = dialog?.querySelector('[data-mlc-pack-confirm-summary]');
      if (!dialog) return;
      this.pendingPackDownload = { files: [...files], button };
      if (summary) {
        const formatter = this.productRoot?.mmcInstance?.formatEstimatedTime?.bind(this.productRoot.mmcInstance);
        const estimate = formatter ? formatter(Math.max(10, Math.ceil(files.length * 0.8))) : `${Math.max(1, Math.ceil(files.length / 75))} minutes`;
        summary.textContent = `${files.length} ${files.length === 1 ? 'asset' : 'assets'} will be downloaded and combined into one ZIP file. Estimated processing time: ${estimate}.`;
      }
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }

    closePackDownloadConfirmation() {
      const dialog = this.root.querySelector('[data-mlc-pack-confirm]');
      if (!dialog) return;
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
      this.pendingPackDownload = null;
    }

    openPackModal(fileId) {
      const modal = this.root.querySelector('[data-mlc-pack-modal]');
      const fileIndex = this.packFiles.findIndex((item) => item.id === fileId);
      if (fileIndex < 0 || !modal) return;
      this.activePackFileIndex = fileIndex;
      this.renderPackModal();
      if (typeof modal.showModal === 'function') modal.showModal();
      else modal.setAttribute('open', '');
      document.body.classList.add('mmc-modal-open');
    }

    movePackModal(direction) {
      if (this.packFiles.length < 2) return;
      this.activePackFileIndex = (this.activePackFileIndex + direction + this.packFiles.length) % this.packFiles.length;
      this.renderPackModal();
    }

    renderPackModal() {
      const file = this.packFiles[this.activePackFileIndex];
      const modal = this.root.querySelector('[data-mlc-pack-modal]');
      if (!file || !modal) return;
      const title = modal.querySelector('[data-mlc-pack-modal-title]');
      const preview = modal.querySelector('[data-mlc-pack-modal-preview]');
      const download = modal.querySelector('[data-mlc-pack-download]');
      const counter = modal.querySelector('[data-mlc-pack-counter]');
      const arrows = modal.querySelectorAll('[data-mlc-pack-previous], [data-mlc-pack-next]');
      title.textContent = file.title;
      preview.replaceChildren();
      this.updatePackFileInformation(file);

      if (file.type === 'video') {
        const video = document.createElement('video');
        video.controls = true;
        video.autoplay = false;
        video.preload = 'metadata';
        if (file.previewUrl) video.poster = file.previewUrl;
        const source = document.createElement('source');
        source.src = file.url;
        if (file.mimeType) source.type = file.mimeType;
        video.append(source);
        video.addEventListener('loadedmetadata', () => {
          if (!file.width && video.videoWidth) file.width = video.videoWidth;
          if (!file.height && video.videoHeight) file.height = video.videoHeight;
          if (this.packFiles[this.activePackFileIndex]?.id === file.id) this.updatePackFileInformation(file);
        }, { once: true });
        preview.append(video);
      } else if (file.previewUrl) {
        const image = document.createElement('img');
        image.src = file.url;
        image.alt = file.title;
        image.addEventListener('load', () => {
          if (!file.width && image.naturalWidth) file.width = image.naturalWidth;
          if (!file.height && image.naturalHeight) file.height = image.naturalHeight;
          if (this.packFiles[this.activePackFileIndex]?.id === file.id) this.updatePackFileInformation(file);
        }, { once: true });
        preview.append(image);
      } else {
        const message = document.createElement('p');
        message.textContent = 'This file does not have a browser preview.';
        preview.append(message);
      }

      download.dataset.url = file.url;
      download.dataset.filename = file.filename;
      if (counter) counter.textContent = `${this.activePackFileIndex + 1} of ${this.packFiles.length}`;
      arrows.forEach((arrow) => { arrow.hidden = this.packFiles.length < 2; });
      this.resolvePackFileSize(file);
    }

    updatePackFileInformation(file) {
      const modal = this.root.querySelector('[data-mlc-pack-modal]');
      if (!modal || !file) return;
      const type = modal.querySelector('[data-mlc-pack-file-type]');
      const size = modal.querySelector('[data-mlc-pack-file-size]');
      const dimensions = modal.querySelector('[data-mlc-pack-dimensions]');
      if (type) type.textContent = this.packFileType(file);
      if (size) size.textContent = file.fileSize ? this.formatFileSize(file.fileSize) : (file.fileSizeChecked ? 'Not available' : 'Calculating…');
      if (dimensions) dimensions.textContent = file.width && file.height ? `${file.width} × ${file.height} px` : 'Not available';
    }

    packFileType(file) {
      const mimeSubtype = file.mimeType?.split('/')[1]?.split(';')[0];
      let extension = mimeSubtype || file.filename?.split('.').pop() || file.url?.split('?')[0].split('.').pop() || file.type || 'file';
      extension = extension.toString().toUpperCase();
      if (extension === 'JPEG') extension = 'JPG';
      if (extension === 'QUICKTIME') extension = 'MOV';
      return extension;
    }

    formatFileSize(bytes) {
      const value = Number(bytes);
      if (!Number.isFinite(value) || value <= 0) return 'Not available';
      const units = ['B', 'KB', 'MB', 'GB'];
      const unitIndex = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
      const amount = value / (1024 ** unitIndex);
      return `${amount >= 10 || unitIndex === 0 ? amount.toFixed(0) : amount.toFixed(1)} ${units[unitIndex]}`;
    }

    async resolvePackFileSize(file) {
      if (!file || file.fileSize || file.fileSizeChecked || file.fileSizePromise) return;
      file.fileSizePromise = fetch(file.url, { method: 'HEAD' })
        .then((response) => {
          const length = Number(response.headers.get('content-length'));
          if (Number.isFinite(length) && length > 0) file.fileSize = length;
        })
        .catch(() => {})
        .finally(() => {
          file.fileSizeChecked = true;
          file.fileSizePromise = null;
          if (this.packFiles[this.activePackFileIndex]?.id === file.id) this.updatePackFileInformation(file);
        });
      await file.fileSizePromise;
    }

    closePackModal() {
      const modal = this.root.querySelector('[data-mlc-pack-modal]');
      if (!modal) return;
      modal.querySelectorAll('video').forEach((video) => video.pause());
      if (typeof modal.close === 'function' && modal.open) modal.close();
      else modal.removeAttribute('open');
      this.activePackFileIndex = -1;
      document.body.classList.remove('mmc-modal-open');
    }

    async downloadPackZip(files, button) {
      if (!files.length || button.disabled) return;
      const status = this.root.querySelector('[data-mlc-pack-status]');
      const original = button.textContent;
      button.disabled = true;
      button.textContent = 'Preparing ZIP…';
      try {
        const zipFiles = [];
        const usedNames = new Map();
        for (let index = 0; index < files.length; index += 1) {
          status.textContent = `Downloading asset ${index + 1} of ${files.length}…`;
          const response = await fetch(files[index].url);
          if (!response.ok) throw new Error(`Asset ${index + 1} failed (${response.status}).`);
          let filename = files[index].filename || `asset-${index + 1}`;
          const key = filename.toLowerCase();
          const duplicate = (usedNames.get(key) || 0) + 1;
          usedNames.set(key, duplicate);
          if (duplicate > 1) filename = `${duplicate}-${filename}`;
          zipFiles.push({ name: filename, data: new Uint8Array(await response.arrayBuffer()) });
        }

        const zipCreator = this.productRoot.mmcInstance;
        if (!zipCreator?.createStoredZip) throw new Error('The ZIP utility is unavailable.');
        const zipBlob = zipCreator.createStoredZip(zipFiles);
        const objectUrl = URL.createObjectURL(zipBlob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = `${this.safeFilename(this.currentFranchise?.title || this.currentLicense?.title || 'license')}-asset-pack.zip`;
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
        status.textContent = `${zipFiles.length} assets downloaded in one ZIP file.`;
      } catch (error) {
        console.error('[Multimedia License Centre]', error);
        status.textContent = `The ZIP could not be created: ${error.message}`;
      } finally {
        button.disabled = false;
        button.textContent = original;
        this.updatePackSelectionControls();
      }
    }

    renderFranchises(license) {
      const section = this.root.querySelector('[data-mlc-franchises]');
      const grid = this.root.querySelector('[data-mlc-franchise-grid]');
      const count = this.root.querySelector('[data-mlc-franchise-count]');
      grid.replaceChildren();

      license.franchises.forEach((franchise) => {
        const article = document.createElement('article');
        article.className = 'mlc-card';
        article.dataset.mlcFranchiseCard = '';
        article.dataset.licenseHandle = license.handle;
        article.dataset.handle = franchise.handle;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mlc-card__button';
        button.setAttribute('aria-label', `View ${franchise.title} franchise assets`);

        const logoWrap = document.createElement('span');
        logoWrap.className = 'mlc-card__logo-wrap';
        const badge = document.createElement('span');
        badge.className = 'mlc-card__asset-badge';
        badge.textContent = 'Media assets';
        logoWrap.append(badge);
        if (franchise.logoUrl) {
          const image = document.createElement('img');
          image.className = 'mlc-card__logo';
          image.src = franchise.logoUrl;
          image.alt = franchise.logoAlt;
          image.loading = 'lazy';
          logoWrap.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'mlc-card__placeholder';
          placeholder.textContent = 'Logo unavailable';
          logoWrap.append(placeholder);
        }

        const title = document.createElement('span');
        title.className = 'mlc-card__title';
        title.textContent = franchise.title;
        const action = document.createElement('span');
        action.className = 'mlc-card__asset-action';
        action.innerHTML = '<span class="mlc-card__asset-action-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6.5h6l2 2h10v10.5H3z"></path><path d="M12 11v5m0 0-2-2m2 2 2-2"></path></svg></span><span>View assets</span>';
        button.append(logoWrap, title, action);
        article.append(button);
        grid.append(article);
      });

      count.textContent = `${license.franchises.length} ${license.franchises.length === 1 ? 'franchise' : 'franchises'}`;
      section.hidden = license.franchises.length === 0;
    }

    async loadEntityProducts(entity, referenceKey, entityLabel) {
      const loadGeneration = ++this.productLoadGeneration;
      const isCurrentLoad = () => loadGeneration === this.productLoadGeneration;
      const loading = this.root.querySelector('[data-mlc-products-loading]');
      const loadingStatus = this.root.querySelector('[data-mlc-products-status]');
      const missing = this.root.querySelector('[data-mlc-no-collection]');
      const grid = this.productRoot.querySelector('[data-mmc-grid]');
      const empty = this.productRoot.querySelector('[data-mmc-empty]');
      const count = this.productRoot.querySelector('[data-mmc-count]');
      const completePanel = this.productRoot.querySelector('[data-mmc-collection-download]');
      grid.replaceChildren();
      empty.hidden = true;
      missing.hidden = true;
      completePanel.hidden = true;
      count.dataset.totalProducts = '0';
      count.textContent = 'Loading products…';

      loading.hidden = false;

      try {
        const index = await this.loadProductIndex(loadingStatus);
        if (!isCurrentLoad()) return;
        const productIds = index
          .filter((product) => product[`${referenceKey}Id`] === entity.id)
          .map((product) => product.id);
        const products = await this.loadProductDetails(productIds, loadingStatus, isCurrentLoad);
        if (!isCurrentLoad()) return;
        products.forEach((product) => grid.append(this.createProductCard(product, entity.title)));
        const totalProducts = products.length;

        count.dataset.totalProducts = totalProducts.toString();
        completePanel.hidden = totalProducts === 0;
        completePanel.dataset.collectionUrl = '';
        completePanel.dataset.collectionHandle = entity.handle;
        completePanel.dataset.collectionTitle = entity.title;
        completePanel.dataset.collectionProducts = totalProducts.toString();
        completePanel.querySelector('[data-mmc-collection-summary]').textContent =
          `Create one ZIP containing all product images from ${totalProducts} products assigned to this ${entityLabel}.`;
        completePanel.querySelector('[data-mmc-complete-eyebrow]').textContent = `Complete ${entityLabel} download`;
        completePanel.querySelector('[data-mmc-download-collection]').textContent = `Download all ${entityLabel} products (.zip)`;
        completePanel.querySelector('[data-mmc-collection-status]').textContent = '';
        if (totalProducts === 0) {
          missing.hidden = false;
          missing.querySelector('h2').textContent = 'No products assigned';
          missing.querySelector('p').textContent = `No Online Store products currently reference this ${entityLabel}.`;
        }
        this.notifyProductsReplaced(totalProducts);
        if (this.productRoot.mmcInstance) {
          this.productRoot.mmcInstance.productProvider = {
            hydrateCards: async (cards, onProgress = () => {}) => onProgress(cards.length, cards.length)
          };
        }
      } catch (error) {
        if (!isCurrentLoad()) return;
        console.error('[Multimedia License Centre]', error);
        missing.hidden = false;
        missing.querySelector('h2').textContent = 'Products could not be loaded';
        missing.querySelector('p').textContent = error.message;
        this.notifyProductsReplaced(0);
      } finally {
        if (isCurrentLoad()) loading.hidden = true;
      }
    }

    async loadProductIndex(status) {
      if (this.productIndex) return this.productIndex;
      if (!this.productIndexPromise) {
        this.productIndexPromise = this.readCache('rubies-mlc-product-relations-v1')
          .then((cached) => {
            if (Array.isArray(cached)) {
              status.textContent = 'Loading saved product relationships…';
              return cached;
            }
            return this.fetchProductIndex(status).then(async (products) => {
              await this.writeCache('rubies-mlc-product-relations-v1', products);
              return products;
            });
          })
          .then((products) => {
            this.productIndex = products;
            return products;
          })
          .finally(() => {
            this.productIndexPromise = null;
          });
      }
      return this.productIndexPromise;
    }

    async fetchProductIndex(status) {
      const products = [];
      let after = null;
      let hasNextPage = true;
      let page = 1;

      while (hasNextPage) {
        status.textContent = 'Preparing products for licenses and franchises…';
        this.updateLoadingProgress(Math.min(95, 88 + page * 0.15), 'Preparing products for licenses and franchises…');
        const data = await this.storefrontQuery(this.productIndexQuery(), { after });
        data.products.nodes.forEach((product) => products.push({
          id: product.id,
          licenseId: product.license?.reference?.id || '',
          franchiseId: product.franchise?.reference?.id || ''
        }));
        hasNextPage = data.products.pageInfo.hasNextPage;
        after = data.products.pageInfo.endCursor;
        page += 1;
      }

      return products;
    }

    productIndexQuery() {
      return `
        query MlcProductIndex($after: String) {
          products(first: 100, after: $after) {
            nodes {
              id
              license: metafield(namespace: "license", key: "license") {
                reference { ... on Metaobject { id } }
              }
              franchise: metafield(namespace: "license", key: "franchise") {
                reference { ... on Metaobject { id } }
              }
            }
            pageInfo { hasNextPage endCursor }
          }
        }
      `;
    }

    async loadProductDetails(ids, status, isCurrentLoad = () => true) {
      if (!this.productDetailsCache) {
        this.productDetailsCache = await this.readCache('rubies-mlc-product-details-v1') || {};
      }
      const missingIds = ids.filter((id) => !this.productDetailsCache[id]);
      for (let offset = 0; offset < missingIds.length; offset += 50) {
        if (!isCurrentLoad()) return [];
        const batch = missingIds.slice(offset, offset + 50);
        status.textContent = `Preparing product images ${offset + 1}–${Math.min(offset + batch.length, missingIds.length)} of ${missingIds.length}…`;
        const data = await this.storefrontQuery(this.productDetailsQuery(), { ids: batch });
        data.nodes.filter(Boolean).forEach((product) => { this.productDetailsCache[product.id] = product; });
      }
      if (missingIds.length) await this.writeCache('rubies-mlc-product-details-v1', this.productDetailsCache);
      return ids.map((id) => this.productDetailsCache[id]).filter(Boolean);
    }

    productDetailsQuery() {
      return `
        query MlcProductDetails($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on Product {
              id handle title vendor
              featuredImage { url altText width height }
              images(first: 250) { nodes { url altText width height } }
              variants(first: 100) { nodes { sku } }
              styleCode: metafield(namespace: "custom", key: "style_code") { value }
            }
          }
        }
      `;
    }

    createProductCard(product, licenseTitle) {
      const article = document.createElement('article');
      const skus = product.variants.nodes.map((variant) => variant.sku).filter(Boolean);
      const styleCode = product.styleCode?.value || skus[0] || '';
      article.className = 'mmc-card';
      article.dataset.mmcCard = '';
      article.dataset.selectionKey = product.handle;
      article.dataset.title = product.title;
      article.dataset.handle = product.handle;
      article.dataset.styleCode = styleCode;
      article.dataset.skus = skus.join(' ');
      article.dataset.license = licenseTitle;

      const select = document.createElement('label');
      select.className = 'mmc-card__select';
      select.title = `Select ${product.title}`;
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.dataset.mmcSelect = '';
      checkbox.setAttribute('aria-label', `Select ${product.title} for download`);
      const selectBox = document.createElement('span');
      selectBox.className = 'mmc-card__select-box';
      selectBox.setAttribute('aria-hidden', 'true');
      selectBox.innerHTML = '<svg viewBox="0 0 16 16" width="12" height="12"><path d="m3 8 3 3 7-7"></path></svg>';
      select.append(checkbox, selectBox);

      const button = document.createElement('button');
      button.className = 'mmc-card__button';
      button.type = 'button';
      button.dataset.mmcOpen = '';
      button.setAttribute('aria-label', `View images for ${product.title}`);
      const imageWrap = document.createElement('span');
      imageWrap.className = 'mmc-card__image-wrap';
      if (product.featuredImage) {
        const image = document.createElement('img');
        image.className = 'mmc-card__image';
        image.src = this.shopifyImageUrl(product.featuredImage.url, 700);
        image.alt = product.featuredImage.altText || product.title;
        image.loading = 'lazy';
        imageWrap.append(image);
      }
      const imageCount = document.createElement('span');
      imageCount.className = 'mmc-card__count';
      imageCount.textContent = `${product.images.nodes.length} ${product.images.nodes.length === 1 ? 'image' : 'images'}`;
      imageWrap.append(imageCount);

      const content = document.createElement('span');
      content.className = 'mmc-card__content';
      const license = document.createElement('span');
      license.className = 'mmc-card__license';
      license.textContent = licenseTitle;
      const title = document.createElement('span');
      title.className = 'mmc-card__title';
      title.textContent = product.title;
      content.append(license, title);
      if (styleCode) {
        const sku = document.createElement('span');
        sku.className = 'mmc-card__sku';
        sku.textContent = `Style: ${styleCode}`;
        content.append(sku);
      }
      const action = document.createElement('span');
      action.className = 'mmc-card__action';
      action.textContent = 'View and download images';
      content.append(action);
      button.append(imageWrap, content);

      const template = document.createElement('template');
      template.dataset.mmcProductData = '';
      const data = document.createElement('div');
      data.dataset.productTitle = product.title;
      data.dataset.productCode = styleCode;
      data.dataset.productLicense = licenseTitle;
      product.images.nodes.forEach((image, index) => {
        const item = document.createElement('div');
        const extension = this.imageExtension(image.url);
        const imageNumber = String(index + 1).padStart(2, '0');
        item.dataset.mmcImage = '';
        item.dataset.preview = this.shopifyImageUrl(image.url, 1200);
        item.dataset.download = image.url;
        item.dataset.alt = image.altText || `${product.title} image ${index + 1}`;
        item.dataset.filename = `${this.safeFilename(styleCode || product.handle)}_${imageNumber}.${extension}`;
        data.append(item);
      });
      template.content.append(data);
      article.append(select, button, template);
      return article;
    }

    shopifyImageUrl(url, width) {
      const imageUrl = new URL(url, window.location.origin);
      imageUrl.searchParams.set('width', width);
      return imageUrl.toString();
    }

    imageExtension(url) {
      const match = new URL(url, window.location.origin).pathname.match(/\.([a-z0-9]+)$/i);
      const extension = match?.[1]?.toLowerCase();
      return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(extension) ? extension : 'jpg';
    }

    notifyProductsReplaced(totalProducts) {
      this.productRoot.dispatchEvent(new CustomEvent('mmc:products-replaced', {
        bubbles: true,
        detail: { totalProducts }
      }));
    }

    async downloadLogo(button) {
      const original = button.textContent;
      button.disabled = true;
      button.textContent = 'Preparing logo…';
      try {
        const response = await fetch(button.dataset.url);
        if (!response.ok) throw new Error(`Logo download failed (${response.status}).`);
        const blob = await response.blob();
        const extension = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg';
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = `${button.dataset.filename}.${extension}`;
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      } catch (error) {
        console.error('[Multimedia License Centre]', error);
        window.open(button.dataset.url, '_blank', 'noopener');
      } finally {
        button.disabled = false;
        button.textContent = original;
      }
    }

    async downloadPackFile(button) {
      const original = button.innerHTML;
      button.disabled = true;
      button.textContent = 'Preparing…';
      try {
        const response = await fetch(button.dataset.url);
        if (!response.ok) throw new Error(`Pack file download failed (${response.status}).`);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = button.dataset.filename || 'download';
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
      } catch (error) {
        console.error('[Multimedia License Centre]', error);
        window.open(button.dataset.url, '_blank', 'noopener');
      } finally {
        button.disabled = false;
        button.innerHTML = original;
      }
    }

    safeFilename(value) {
      return value.toString().trim().replace(/[^a-z0-9._-]+/gi, '_').replace(/^_+|_+$/g, '') || 'license';
    }
  }

  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-mlc]').forEach((root) => {
      if (root.dataset.mlcInitialised === 'true') return;
      root.dataset.mlcInitialised = 'true';
      new MultimediaLicenseCentre(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
  else initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
